import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { HeaderComponent } from '../../components/header/header.component';
import { GlobalService } from '../../services/global.service';
import { ExamService } from '../../services/exam.service';
import { HttpService } from '../../services/http.service';

@Component({
  selector: 'app-student-script-annotations-page',
  imports: [CommonModule, HeaderComponent],
  templateUrl: './student-script-annotations-page.component.html',
})
export class StudentScriptAnnotationsPageComponent {
  page_name = 'Script View';
  params: any = {};
  is_loading = false;
  is_ai_scored = false;
  is_paginating = false;
  level: string | null = null;

  // One entry per script page image, each carrying only the annotations that
  // belong to that page (positioned relative to that page, not the whole
  // stacked document) so printing can break one page per script page without
  // annotations drifting onto the wrong page.
  pages: { image: string; annotations: any[] }[] = [];

  // Question-wise obtained/max marks, rendered as a table on an extra page
  // appended after the script page images.
  question_marks: { question_number: string; obtained: any; max: any }[] = [];

  private raw_annotations: any[] = [];
  private loaded_image_indexes = new Set<number>();

  // Same icon-image map as the evaluator's evaluation page, so drawing/ruler/protractor
  // annotations render identically (read-only) for the student.
  icon_images: any = {
    ruler: 'https://cdn.pixabay.com/photo/2015/11/05/05/46/ruler-1023727_1280.png',
    protractor: 'https://www.pngmart.com/files/23/Protractor-PNG-HD.png',
    'bx bx-chat': null,
    'bx bx-check': null,
    'bx bx-x': null,
  };

  constructor(
    public gs: GlobalService,
    public es: ExamService,
    public http: HttpService,
    public ar: ActivatedRoute,
    public router: Router,
  ) { }

  async ngOnInit() {
    this.ar.queryParams.subscribe(async (params) => {
      this.params = { ...params };
    });

    const script_id = this.params.script_id;
    if (!script_id) {
      this.gs.go_back();
      return;
    }

    this.is_loading = true;
    try {
      const res: any = await this.es.get_published_annotations(script_id);
      const data = res?.data;
      this.level = data?.level ?? null;
      this.is_ai_scored = !!data?.is_ai_scored;

      // Page order must match the evaluator's view, which sorts by file name rather than by
      // ScriptImage id (see evaluation-page.component.ts). Script and supplement pages are
      // inserted in whatever order they were ingested, so id order interleaves them wrongly
      // — the annotation y-coordinates were recorded against the file-name order.
      const images: string[] = [...(data?.images || [])]
        .sort((a: any, b: any) => {
          const name_a = a.image_path?.split('/').pop() || '';
          const name_b = b.image_path?.split('/').pop() || '';
          return name_a.localeCompare(name_b);
        })
        .map((img: any) => img.image_path);
      const questions: any[] = data?.questions || [];

      this.raw_annotations = (data?.annotations || []).map((a: any) => {
        const icon = a.type || 'bx bx-check';
        let img = this.icon_images[icon] || null;

        if (icon === 'drawing' && a.svg) {
          try {
            const blob = new Blob([a.svg], { type: 'image/svg+xml' });
            img = URL.createObjectURL(blob);
          } catch (e) {
            console.error('Error creating object URL for drawing', e);
          }
        } else if ((icon === 'ruler' || icon === 'protractor') && a.svg) {
          img = a.svg;
        } else if (a.img) {
          img = a.img;
        }

        const question = questions.find((q: any) => q.question_number === a.question_number);

        return {
          icon,
          img,
          x: Number(a.x) || 0,
          y: Number(a.y) || 0,
          question_number: a.question_number || null,
          given_mark: question ? (question.rl === 'true' ? 'RL' : question.nr === 'true' ? 'NR' : question.given_mark) : null,
          message: a.message || '',
        };
      });

      // No sorting here on purpose — the backend already returns questions through the same
      // sort_questions_by_number() the evaluator's page uses, so the student's table sequence
      // matches the evaluation page exactly (including roman-numeral parts like "2.i").
      this.question_marks = questions.map((q: any) => ({
        question_number: q.question_number,
        obtained: q.rl === 'true' ? 'RL' : q.nr === 'true' ? 'NR' : q.given_mark,
        max: q.marks,
      }));

      // Pages start with no annotations assigned yet — on_page_image_loaded()
      // fills them in once every image has actually rendered, since we need
      // each image's real offsetTop/offsetHeight to know which page an
      // annotation's y falls on (same approach evaluation-page.component.ts's
      // get_page_index_from_y() uses).
      this.pages = images.map((image) => ({ image, annotations: [] }));
      this.is_paginating = images.length > 0;
    } catch (error: any) {
      this.gs.toastr_shows_function(error?.error?.message || 'Failed to load script', 'Error', 'error');
      this.gs.go_back();
    } finally {
      this.is_loading = false;
    }
  }

  on_page_image_loaded(index: number) {
    this.loaded_image_indexes.add(index);
    if (this.loaded_image_indexes.size >= this.pages.length) {
      this.build_pages();
    }
  }

  private build_pages() {
    const boundaries = this.pages.map((_, i) => {
      const img_el = document.getElementById('script-page-img-' + i) as HTMLElement | null;
      const parent = img_el?.parentElement;
      const top = parent?.offsetTop ?? 0;
      const bottom = top + (img_el?.offsetHeight ?? 0);
      return { top, bottom };
    });

    const pages = this.pages.map((p) => ({ image: p.image, annotations: [] as any[] }));
    for (const item of this.raw_annotations) {
      let page_index = boundaries.findIndex((b) => item.y >= b.top && item.y < b.bottom);
      if (page_index === -1) {
        page_index = item.y < (boundaries[0]?.top ?? 0) ? 0 : boundaries.length - 1;
      }
      const rel_y = item.y - (boundaries[page_index]?.top ?? 0);
      pages[page_index].annotations.push({ ...item, y: rel_y });
    }

    this.pages = pages;
    this.is_paginating = false;
  }



  // Prints the actual on-screen #printable-script element (see the global
  // @media print rules in src/styles.css) instead of rebuilding a copy from
  // scratch, so the downloaded PDF always matches the preview exactly. Each
  // .script-page inside it breaks onto its own physical print page (also in
  // src/styles.css), so a script page's annotations can never drift onto the
  // wrong printed page.
  download_script() {
    if (!this.pages.length || this.is_paginating) return;
    window.print();
  }
}
